// src/schemas/schema-utils.ts
import { Type } from "@sinclair/typebox";

// src/schemas/discriminated-unions.ts
function branchNameConst(branch) {
  if (typeof branch !== "object" || branch === null || Array.isArray(branch))
    return void 0;
  const name = branch.properties?.name;
  return typeof name?.const === "string" ? name.const : void 0;
}
function isVersionedBranch(branch) {
  const version = branch.properties?.version;
  return typeof version?.const === "string";
}
function branchRequiresName(branch) {
  return Array.isArray(branch.required) && branch.required.includes("name");
}
function groupByName(branches) {
  const groups = /* @__PURE__ */ new Map();
  for (const branch of branches) {
    const name = branchNameConst(branch);
    const group = groups.get(name);
    if (group) group.push(branch);
    else groups.set(name, [branch]);
  }
  return groups;
}
function nameEntry(name, group) {
  const source = group.find(
    (b) => !isVersionedBranch(b) && typeof b.description === "string"
  ) ?? group.find((b) => typeof b.description === "string");
  return {
    const: name,
    type: "string",
    ...source ? { description: source.description } : {}
  };
}
function restructureNameDiscriminatedUnions(schema) {
  const visited = /* @__PURE__ */ new WeakSet();
  function walk(node) {
    if (typeof node !== "object" || node === null) return;
    if (visited.has(node)) return;
    visited.add(node);
    if (Array.isArray(node)) {
      node.forEach(walk);
      return;
    }
    const obj = node;
    const anyOf = obj.anyOf;
    const isCandidate = Array.isArray(anyOf) && anyOf.length >= 2 && anyOf.every(
      (b) => branchNameConst(b) !== void 0 && branchRequiresName(b)
    ) && obj.properties === void 0 && obj.allOf === void 0 && obj.if === void 0 && obj.required === void 0 && // A sibling `additionalProperties` evaluates against the node's own
    // (absent) `properties`; declaring `name` here would change what it
    // rejects, so such unions are left alone.
    obj.additionalProperties === void 0 && (obj.type === void 0 || obj.type === "object");
    const groups = isCandidate ? groupByName(anyOf) : void 0;
    if (groups && groups.size >= 2) {
      obj.type = "object";
      obj.required = ["name"];
      obj.properties = {
        name: {
          description: "Component or registered plugin to render.",
          anyOf: [...groups.entries()].map(
            ([name, group]) => nameEntry(name, group)
          )
        }
      };
      obj.allOf = [...groups.entries()].map(([name, group]) => ({
        if: {
          properties: { name: { const: name } },
          required: ["name"]
        },
        then: group.length === 1 ? group[0] : { anyOf: group }
      }));
      delete obj.anyOf;
    }
    for (const value of Object.values(obj)) walk(value);
  }
  walk(schema);
}
function unionBranches(schema) {
  if (typeof schema !== "object" || schema === null) return [];
  const obj = schema;
  if (Array.isArray(obj.anyOf)) {
    return obj.anyOf.filter(
      (b) => typeof b === "object" && b !== null
    );
  }
  if (Array.isArray(obj.allOf)) {
    return obj.allOf.flatMap((entry) => {
      const then = entry?.then;
      if (typeof then !== "object" || then === null) return [];
      const inner = then.anyOf;
      return Array.isArray(inner) ? inner.filter(
        (b) => typeof b === "object" && b !== null
      ) : [then];
    });
  }
  return [];
}

// src/schemas/schema-utils.ts
function replaceRefs(obj, target, replacement) {
  if (typeof obj !== "object" || obj === null) return;
  if (Array.isArray(obj)) {
    obj.forEach((item) => {
      if (typeof item === "object" && item !== null) {
        replaceRefs(item, target, replacement);
      }
    });
    return;
  }
  if (obj.$ref === target) {
    obj.$ref = replacement;
  }
  for (const value of Object.values(obj)) {
    if (typeof value === "object" && value !== null) {
      replaceRefs(value, target, replacement);
    }
  }
}
function fixSchemaReferences(schema, rootDefinitionName = "ComponentDefinition") {
  const definitionNames = new Set(
    Object.keys(
      schema.definitions ?? {}
    )
  );
  const definitionRef = (name) => {
    const target = definitionNames.has(name) ? name : rootDefinitionName;
    return definitionNames.has(target) ? `#/definitions/${target}` : void 0;
  };
  const isBareDefinitionRef = (value) => typeof value === "string" && (/^T\d+$/.test(value) || value === rootDefinitionName || definitionNames.has(value));
  function traverse(obj, path = "") {
    if (typeof obj !== "object" || obj === null) return;
    for (const [key, value] of Object.entries(obj)) {
      const currentPath = path ? `${path}.${key}` : key;
      if (value && typeof value === "object") {
        const schemaValue = value;
        if (schemaValue.type === "array" && schemaValue.items && Object.keys(schemaValue.items).length === 0 && definitionNames.has(rootDefinitionName)) {
          schemaValue.items = {
            $ref: `#/definitions/${rootDefinitionName}`
          };
        }
        if (schemaValue.type === "array" && schemaValue.items && typeof schemaValue.items === "object" && "$ref" in schemaValue.items && isBareDefinitionRef(
          schemaValue.items.$ref
        )) {
          const name = schemaValue.items.$ref;
          const ref = definitionRef(name);
          schemaValue.items = ref ? { $ref: ref } : {};
        }
        if (isBareDefinitionRef(schemaValue.$ref)) {
          const ref = definitionRef(schemaValue.$ref);
          if (ref) schemaValue.$ref = ref;
          else delete schemaValue.$ref;
        }
        if (key === "$id" && isBareDefinitionRef(value) && currentPath !== `definitions.${value}.$id`) {
          delete obj[key];
          continue;
        }
        traverse(value, currentPath);
      }
    }
  }
  traverse(schema);
}
function convertToJsonSchema(schema, options = {}) {
  const {
    $schema = "http://json-schema.org/draft-07/schema#",
    $id,
    title,
    description,
    definitions = {},
    enrich
  } = options;
  const schemaJson = JSON.parse(JSON.stringify(schema));
  if (schemaJson.$id && typeof schemaJson.$id === "string" && /^T\d+$/.test(schemaJson.$id)) {
    const recursiveId = schemaJson.$id;
    delete schemaJson.$id;
    replaceRefs(schemaJson, recursiveId, "#");
  }
  const extractedDefinitions = { ...definitions };
  function extractRecursiveSchemas(obj, path = "") {
    if (typeof obj !== "object" || obj === null) return;
    for (const [key, value] of Object.entries(obj)) {
      if (value && typeof value === "object") {
        const schemaValue = value;
        if (schemaValue.$id && typeof schemaValue.$id === "string") {
          const definitionName = schemaValue.$id;
          if (path !== `definitions.${definitionName}`) {
            const { $id: _id, ...schemaWithoutId } = schemaValue;
            extractedDefinitions[definitionName] = schemaWithoutId;
            obj[key] = { $ref: `#/definitions/${definitionName}` };
            extractRecursiveSchemas(
              schemaWithoutId,
              `definitions.${definitionName}`
            );
            continue;
          }
        }
        extractRecursiveSchemas(
          value,
          path ? `${path}.${key}` : key
        );
      }
    }
  }
  extractRecursiveSchemas(schemaJson);
  const jsonSchema = { $schema };
  if ($id) jsonSchema.$id = $id;
  Object.assign(jsonSchema, schemaJson);
  jsonSchema.$schema = $schema;
  if ($id) jsonSchema.$id = $id;
  if (title !== void 0) jsonSchema.title = title;
  if (description !== void 0) jsonSchema.description = description;
  if (Object.keys(extractedDefinitions).length > 0) {
    jsonSchema.definitions = extractedDefinitions;
  }
  fixSchemaReferences(jsonSchema);
  enrich?.(jsonSchema);
  restructureNameDiscriminatedUnions(jsonSchema);
  return jsonSchema;
}
function createComponentSchema(name, config, containerNames, componentDefinitionSchema, rootDefinitionName = "ComponentDefinition") {
  const componentStructure = {
    $schema: "http://json-schema.org/draft-07/schema#",
    $id: `${name}.schema.json`,
    title: config.title,
    description: config.description,
    type: "object",
    required: ["name", "props"],
    properties: {
      name: {
        type: "string",
        const: name,
        description: `Component name identifier (must be "${name}")`
      },
      id: {
        type: "string",
        description: "Optional unique identifier for the component"
      },
      props: JSON.parse(JSON.stringify(config.schema))
    }
  };
  if (containerNames.includes(name)) {
    componentStructure.properties.children = {
      type: "array",
      description: "Children within this container",
      items: {
        $ref: `#/definitions/${rootDefinitionName}`
      }
    };
    if (componentDefinitionSchema) {
      componentStructure.definitions = {
        [rootDefinitionName]: JSON.parse(
          JSON.stringify(componentDefinitionSchema)
        )
      };
    }
  }
  fixSchemaReferences(componentStructure, rootDefinitionName);
  componentStructure.additionalProperties = false;
  return componentStructure;
}
async function exportSchemaToFile(schema, outputPath, options = {}) {
  const { prettyPrint = true } = options;
  const jsonSchema = prettyPrint ? JSON.stringify(schema, null, 2) : JSON.stringify(schema);
  const fs = await import("fs/promises");
  await fs.writeFile(outputPath, jsonSchema, "utf-8");
}
function createComponentSchemaObject(component, recursiveRef) {
  const schema = {
    name: Type.Literal(component.name),
    id: Type.Optional(Type.String()),
    enabled: Type.Optional(
      Type.Boolean({
        default: true,
        description: "When false, this component is filtered out and not rendered. Defaults to true."
      })
    )
  };
  if (component.special?.hasSchemaField) {
    schema.$schema = Type.Optional(Type.String({ format: "uri" }));
  }
  schema.props = component.propsSchema;
  if (component.hasChildren && recursiveRef) {
    schema.children = Type.Optional(Type.Array(recursiveRef));
  }
  return Type.Object(schema, { additionalProperties: false });
}

export {
  restructureNameDiscriminatedUnions,
  unionBranches,
  fixSchemaReferences,
  convertToJsonSchema,
  createComponentSchema,
  exportSchemaToFile,
  createComponentSchemaObject
};
//# sourceMappingURL=chunk-LFHU4EOV.js.map